import {
  IDestination,
  IGoodCategoriesListParams,
  IListParams,
  ILoadingUnitsListParams,
  IProvidersListParams,
  IQuotationPayload,
  IRecipient,
  IRequestListParams,
  IWvUnitsListParams,
  PostedLineItem,
} from '@common/interfaces';
import IFreightRepository from '@common/interfaces/FreightRepository';
import { IDetailsUpdate } from '@common/interfaces/Common';
import AxiosRepository from './axios.repository';

class FreightRepository extends AxiosRepository implements IFreightRepository {
  async getGoodCategoriesList(facilityId: number, params?: IGoodCategoriesListParams) {
    const { data } = await this.get(`facilities/${facilityId}/bookings/good-categories/`, {
      params,
    });
    return data;
  }

  async getLoadingUnitsList(facilityId: number, params?: ILoadingUnitsListParams) {
    const { data } = await this.get(`facilities/${facilityId}/bookings/loading-units/`, {
      params,
    });
    return data;
  }

  async getWvUnitsList(facilityId: number, params?: IWvUnitsListParams) {
    const { data } = await this.get(`facilities/${facilityId}/bookings/weight-volume-units/`, {
      params,
    });
    return data;
  }

  async getRequestsList(facilityId: number, params: IRequestListParams) {
    const { data } = await this.get(`facilities/${facilityId}/bookings/`, {
      params,
    });
    return data;
  }

  async getAllAssignmentsReportFile(facilityId: number, params: IListParams) {
    const { data } = await this.get(`facilities/${facilityId}/bookings/assignments/export/all/`, {
      responseType: 'arraybuffer',
      params,
    });

    return data;
  }

  async getSelectedAssignmentsReportFile(facilityId: number, params: IListParams) {
    const { data } = await this.get(
      `facilities/${facilityId}/bookings/assignments/export/selected/`,
      {
        responseType: 'arraybuffer',
        params,
      },
    );

    return data;
  }

  async getAssignmentsList(facilityId: number, params: IRequestListParams) {
    const { data } = await this.get(`facilities/${facilityId}/bookings/assignments`, {
      params,
    });
    return data;
  }

  async getRequestDetails(facilityId: number, freightId: string) {
    const { data } = await this.get(`facilities/${facilityId}/bookings/${freightId}/`);
    return data;
  }

  async getProvidersList(params: IProvidersListParams) {
    const { data } = await this.get(`providers/`, {
      params,
    });
    return data;
  }

  async getProvidersQuotation(facilityId: number, booking_uuid: string) {
    const { data } = await this.get(
      `facilities/${facilityId}/bookings/${booking_uuid}/providers-quotations/`,
    );
    return data;
  }

  async updateRequestDetails(facilityId: number, freightId: string, payload: IDetailsUpdate) {
    const { data } = await this.patch(`facilities/${facilityId}/bookings/${freightId}/`, {
      ...payload,
    });
    return data;
  }

  async updateDestination(
    facilityId: number,
    destinationUuid: string,
    payload: Partial<IDestination>,
  ) {
    const { data } = await this.patch(
      `facilities/${facilityId}/bookings/destinations/${destinationUuid}/`,
      {
        ...payload,
      },
    );
    return data;
  }

  async updateRecipient(facilityId: number, recipientUuid: string, payload: Partial<IRecipient>) {
    const { data } = await this.patch(
      `facilities/${facilityId}/bookings/sender-recipient/${recipientUuid}/`,
      {
        ...payload,
      },
    );
    return data;
  }

  async sendQuotationRequest(facilityId: number, freightId: string, payload: IQuotationPayload) {
    const { data } = await this.post(
      `facilities/${facilityId}/bookings/${freightId}/quotation-requests/`,
      {
        ...payload,
      },
    );
    return data;
  }

  async sendProvidersQuotation(facilityId: number, booking_id: string, payload: string[]) {
    const { data } = await this.post(
      `facilities/${facilityId}/bookings/${booking_id}/providers-quotations/`,
      {
        uuid: payload,
      },
    );
    return data;
  }

  async sendLineItemRequest(facilityId: number, freightId: string, payload: PostedLineItem) {
    const { data } = await this.post(`facilities/${facilityId}/bookings/${freightId}/line-items/`, {
      ...payload,
    });
    return data;
  }

  async deleteLineItems(facilityId: number, lineItemUuid: string) {
    await this.delete(`facilities/${facilityId}/bookings/line-items/${lineItemUuid}/`);
  }
}

export default FreightRepository;
